import { Chip, Tooltip } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SmartDisplayOutlinedIcon from "@mui/icons-material/SmartDisplayOutlined";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainHeading from "../../../components/main-heading.component";
import { _get, _post } from "../../../services/api.service";
import "../content-engine.css";
import { contentEngineService } from "../content-engine.service";
import { getRefinementIcon } from "../../../utils/refinementIcons";
import "../../video-create/CreateVideo.css";
import ManualEditDialog from "./manual-edit-dialog";
import RefinementResultDialog from "./refinement-result-dialog";
import "./ScriptDetail.css";

const BLOCK_LABELS: Record<string, string> = {
  hook: "HOOK",
  body: "BODY",
  cta: "CTA",
};

type RefineSuggestion = { label: string; instruction: string };
type RefineSuggestionsMap = Record<string, RefineSuggestion[]>;

const ScriptDetail: React.FC = () => {
  const navigate = useNavigate();
  const steps = [
    { label: "Topic", description: "Enter your topic idea", path: "/content-engine" },
    { label: "Format", description: "Choose your content format", path: "/content-engine/select-format" },
    { label: "Scripts", description: "Pick from generated scripts", path: "/content-engine/choose-script" },
    { label: "Refinement", description: "Refine and finalize your script", path: "/content-engine/script-detail" },
  ];
  const currentStep = 3;
  const [script, setScript] = useState<any>(null);
  const [scripts, setScripts] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [refinementDialogOpen, setRefinementDialogOpen] = useState(false);
  const [refinementLoading, setRefinementLoading] = useState(false);
  const [refinementResult, setRefinementResult] = useState<any>(null);
  const [activating, setActivating] = useState(false);
  const [manualEditOpen, setManualEditOpen] = useState(false);
  const [manualEditSaving, setManualEditSaving] = useState(false);
  const [manualEditError, setManualEditError] = useState<string | null>(null);
  const [topicName, setTopicName] = useState("");
  const [refineSuggestions, setRefineSuggestions] = useState<RefineSuggestionsMap>({});
  const [expandedRefineBlocks, setExpandedRefineBlocks] = useState<Set<string>>(new Set());
  const [blockInstructions, setBlockInstructions] = useState<Record<string, string>>({});
  const [turningToVideo, setTurningToVideo] = useState(false);

  const blocks = script?.latest_version?.blocks || [];
  const totalSecs = Math.round(blocks.reduce((s: number, b: any) => s + (parseFloat(b.estimated_duration) || 0), 0));
  const totalWords = blocks.reduce((s: number, b: any) => {
    const text = [b.title, b.narration, b.caption].filter(Boolean).join(" ");
    return s + text.split(/\s+/).filter(Boolean).length;
  }, 0);
  const currentBlocks = blocks.length > 0 ? blocks : null;
  const newResultBlocks = refinementResult?.version?.blocks || null;

  const toggleChip = (group: string, instruction: string) => {
    setBlockInstructions((prev) => {
      const current = prev[group] || "";
      const lines = current.split("\n").filter(Boolean);
      const idx = lines.indexOf(instruction);
      if (idx >= 0) {
        lines.splice(idx, 1);
      } else {
        lines.push(instruction);
      }
      return { ...prev, [group]: lines.join("\n") };
    });
  };

  const selectedChips = useMemo(() => {
    const set = new Set<string>();
    for (const text of Object.values(blockInstructions)) {
      for (const line of text.split("\n")) {
        const trimmed = line.trim();
        if (trimmed) set.add(trimmed);
      }
    }
    return set;
  }, [blockInstructions]);

  useEffect(() => {
    const selected = contentEngineService.getSelectedScript();
    const topic = contentEngineService.getSelectedTopic();
    if (!selected || !topic) {
      navigate("/content-engine/choose-script", { replace: true });
      return;
    }
    setTopicName(topic.name);
    const cached = contentEngineService.getGeneratedContents(topic.id) || [];
    setScripts(cached);
    const idx = cached.findIndex((s: any) => s.id === selected.id);
    setCurrentIndex(idx >= 0 ? idx : 0);
    setScript(selected);
  }, [navigate]);

  const formatSlug = script?.content_format?.slug;

  useEffect(() => {
    if (!formatSlug) {
      setRefineSuggestions({});
      return;
    }
    _get(`/content-engine/content-formats/${formatSlug}/refinement-suggestions/`, {}, true).then((data: any) => {
      if (!data) {
        setRefineSuggestions({});
        return;
      }
      const map: RefineSuggestionsMap = {};
      for (const g of data) {
        const key = BLOCK_LABELS[g.block_type] || g.name;
        map[key] = g.suggestions;
      }
      setRefineSuggestions(map);
    }).catch(() => {
      setRefineSuggestions({});
    });
  }, [formatSlug]);

  const goTo = (index: number) => {
    const s = scripts[index];
    if (!s) return;
    contentEngineService.setSelectedScript(s);
    setScript(s);
    setCurrentIndex(index);
    setBlockInstructions({});
    setExpandedRefineBlocks(new Set());
  };

  const getFullText = (blocksList: any[]) => {
    return blocksList.map((b: any) => {
      const parts = [b.title, b.narration, b.caption].filter(Boolean);
      return parts.join("\n\n");
    }).join("\n\n");
  };

  const handleCopy = useCallback(() => {
    if (!script) return;
    const text = getFullText(blocks);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [script, blocks]);

  const refinementLabels = useMemo(() => {
    const result: { label: string; group: string }[] = [];
    const seenLabels = new Set<string>();

    const instructionToLabel = new Map<string, string>();
    const instructionToGroup = new Map<string, string>();
    for (const [group, chips] of Object.entries(refineSuggestions)) {
      for (const chip of chips) {
        instructionToLabel.set(chip.instruction, chip.label);
        instructionToGroup.set(chip.instruction, group);
      }
    }

    for (const text of Object.values(blockInstructions)) {
      for (const line of text.split("\n")) {
        const trimmed = line.trim();
        if (!trimmed) continue;
      const label = instructionToLabel.get(trimmed);
      if (label && instructionToGroup.get(trimmed)) {
        if (seenLabels.has(label)) continue;
        seenLabels.add(label);
        result.push({ label, group: instructionToGroup.get(trimmed)! });
      } else {
        if (seenLabels.has(trimmed)) continue;
        seenLabels.add(trimmed);
        result.push({ label: trimmed, group: "Manual" });
      }
    }
    }
    return result;
  }, [selectedChips, blockInstructions, refineSuggestions]);

  const blockInstructionList = useMemo(() => {
    const result: { block_id: number; instruction: string }[] = [];
    const blocks = script?.latest_version?.blocks || [];
    for (const block of blocks) {
      const blockKey = block.id || `block-${blocks.indexOf(block)}`;
      const text = (blockInstructions[blockKey] || "").trim();
      if (text && block.id) {
        result.push({ block_id: block.id, instruction: text });
      }
    }
    return result;
  }, [blockInstructions, script]);

  const handleBlockRefine = useCallback(async () => {
    const instructions = blockInstructionList;
    if (!script || instructions.length === 0) return;
    const versionId = script.latest_version?.id;
    if (!versionId) return;

    setRefinementDialogOpen(true);
    setRefinementLoading(true);
    setRefinementResult(null);

    try {
      const response = await _post(
        `/content-engine/versions/${versionId}/refine/`,
        { instructions },
        {},
        false,
        true,
      );
      setRefinementResult(response);
    } catch {
      console.error("Error refining creative");
      setRefinementDialogOpen(false);
    } finally {
      setRefinementLoading(false);
    }
  }, [script, blockInstructionList]);

  const handleSelectVersion = useCallback(async (useNew: boolean) => {
    if (useNew && refinementResult?.version) {
      setActivating(true);
      try {
        const response = await _post(
          `/content-engine/versions/${refinementResult.version.id}/activate/`,
          {},
          {},
          false,
        );
        if (!response) {
          setActivating(false);
          return;
        }
        const updated = response;
        setScript(updated);
        const updatedScripts = [...scripts];
        updatedScripts[currentIndex] = updated;
        setScripts(updatedScripts);
        const topic = contentEngineService.getSelectedTopic();
        if (topic) {
          contentEngineService.setGeneratedContents(topic.id, updatedScripts);
        }
        contentEngineService.setSelectedScript(updated);
        setBlockInstructions({});
        setExpandedRefineBlocks(new Set());
      } catch {
        console.error("Error activating refined version");
      } finally {
        setActivating(false);
      }
    }
    setBlockInstructions({});
    setExpandedRefineBlocks(new Set());
    setRefinementDialogOpen(false);
    setRefinementResult(null);
  }, [refinementResult, scripts, currentIndex]);

  const handleSaveManualEdit = useCallback(async (updatedBlocks: any[]) => {
    if (!script) return;
    const versionId = script.latest_version?.id;
    if (!versionId) return;
    setManualEditSaving(true);
    setManualEditError(null);
    try {
      const payload = { blocks: updatedBlocks };
      const response = await _post(
        `/content-engine/versions/${versionId}/manual-edit/`,
        payload,
        {},
        false,
        true,
      );
      if (!response) {
        setManualEditError("Failed to save changes. Please try again.");
        return;
      }
      const updated = response;
      setScript(updated);
      const updatedScripts = [...scripts];
      updatedScripts[currentIndex] = updated;
      setScripts(updatedScripts);
      const topic = contentEngineService.getSelectedTopic();
      if (topic) {
        contentEngineService.setGeneratedContents(topic.id, updatedScripts);
      }
      contentEngineService.setSelectedScript(updated);
      setManualEditOpen(false);
    } catch {
      setManualEditError("Failed to save changes. Please try again.");
    } finally {
      setManualEditSaving(false);
    }
  }, [script, scripts, currentIndex]);

  const handleTurnToVideo = useCallback(async () => {
    const versionId = script?.latest_version?.id;
    if (!versionId || turningToVideo) return;
    setTurningToVideo(true);
    try {
      const response = await _post(
        "video-engine/projects/",
        { content_version_id: versionId },
        {},
        false,
        true,
      );
      if (response?.id) {
        navigate(`/video-projects/${response.id}`);
      }
    } finally {
      setTurningToVideo(false);
    }
  }, [script, navigate, turningToVideo]);

  if (!script) return null;

  return (
    <>
      <ManualEditDialog
        open={manualEditOpen}
        onClose={() => { setManualEditOpen(false); setManualEditError(null); }}
        onSave={handleSaveManualEdit}
        blocks={currentBlocks || []}
        saving={manualEditSaving}
        saveError={manualEditError}
        versionNumber={script?.latest_version?.version}
        contentFormatSlug={script?.content_format?.slug}
      />
      <RefinementResultDialog
        open={refinementDialogOpen}
        onClose={() => { setRefinementDialogOpen(false); setRefinementResult(null); setBlockInstructions({}); setExpandedRefineBlocks(new Set()); }}
        loading={refinementLoading}
        activating={activating}
        currentBlocks={currentBlocks}
        newBlocks={newResultBlocks}
        onSelectVersion={handleSelectVersion}
        refinementLabels={refinementLabels}
        topicName={topicName}
      />
      <div className="container-fluid script-detail-page">
      <MainHeading
        subTitle="Preview, refine, and turn your script into a video"
        title={
          <span style={{ display: "inline-flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
            {topicName.charAt(0).toUpperCase() + topicName.slice(1)}
            {script?.content_format?.name && (
              <sup style={{ lineHeight: 1 }}>
                <Chip
                  label={script.content_format.name.toUpperCase()}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 700,
                    borderRadius: "4px",
                    bgcolor: "rgba(35, 100, 232, 0.1)",
                    color: "#2364e8",
                    "& .MuiChip-label": { px: 0.6 },
                  }}
                />
              </sup>
            )}
          </span>
        }
      />
      <div className="cv-workspace">
        <aside className="cv-progress-panel" aria-label="Content engine progress">
          <div className="cv-progress-caption">CONTENT ENGINE</div>
          {steps.map((step, index) => {
            const complete = index < currentStep;
            const active = index === currentStep;
            return (
              <div
                key={step.label}
                className={`cv-progress-step${complete ? " complete" : ""}${active ? " active" : ""}${complete ? " clickable" : ""}`}
                onClick={() => complete && navigate(step.path)}
                onKeyDown={(e) => complete && e.key === "Enter" && navigate(step.path)}
                role={complete ? "button" : undefined}
                tabIndex={complete ? 0 : undefined}
              >
                <div className="cv-progress-rail">
                  <span>{complete ? "✓" : index + 1}</span>
                </div>
                <div>
                  <div className="cv-progress-title">{step.label}</div>
                  <div className="cv-progress-description">{step.description}</div>
                </div>
              </div>
            );
          })}
        </aside>

        <button
          type="button"
          className="sd-btn-link-back ce-mobile-nav-link"
          onClick={() => navigate("/content-engine/choose-script")}
        >
          ← Back to Scripts
        </button>

        <section className="cv-creator-card">
          <div className="cv-card-inner">
              {scripts.length > 1 && (
                <div className="sd-creative-nav">
                  <button className="sd-nav-btn" disabled={currentIndex === 0} onClick={() => goTo(currentIndex - 1)}>← Previous</button>
                  <span className="sd-nav-label">Script {currentIndex + 1} of {scripts.length}</span>
                  <button className="sd-nav-btn" disabled={currentIndex === scripts.length - 1} onClick={() => goTo(currentIndex + 1)}>Next →</button>
                </div>
              )}

              <div className="sd-subtitle-row">
                <p className="subtitle">Script Preview</p>
                <span className="sd-style-tag">
                  {script.tag_display || ""}
                </span>
                <button
                  type="button"
                  className="sd-btn-link-back ce-desktop-nav-link"
                  onClick={() => navigate("/content-engine/choose-script")}
                >
                  ← Back to Scripts
                </button>
              </div>

              <div className="sd-block-legend">
                <div className="sd-legend-item"><span className="sd-legend-badge sd-legend-badge-t">T</span> Title — Large heading shown on screen</div>
                <div className="sd-legend-item"><span className="sd-legend-badge sd-legend-badge-n">N</span> Narration — Spoken by the AI voice</div>
                <div className="sd-legend-item"><span className="sd-legend-badge sd-legend-badge-c">C</span> Caption — Highlighted text overlay</div>
              </div>

              <div className="sd-card">
                <div className="sd-card-actions">
                  <div className="sd-card-stats">
                    {totalSecs > 0 && (
                      <>
                        <span className="sd-card-stat sd-card-stat--desktop">
                          ⏱ ~{totalSecs >= 60 ? `${Math.floor(totalSecs / 60)}m ${totalSecs % 60}s` : `${totalSecs}s`} total duration
                        </span>
                        <span className="sd-card-stat sd-card-stat--mobile" title="Estimated duration">
                          <span className="sd-card-stat-value">
                            ⏱ ~{totalSecs >= 60 ? `${Math.floor(totalSecs / 60)}m ${totalSecs % 60}s` : `${totalSecs}s`}
                          </span>
                        </span>
                      </>
                    )}
                    {totalWords > 0 && (
                      <>
                        <span className="sd-card-stat sd-card-stat--desktop">
                          <TextFieldsIcon sx={{ fontSize: 14, mr: 0.4, verticalAlign: "text-top" }} /> {totalWords} words
                        </span>
                        <span className="sd-card-stat sd-card-stat--mobile" title="Word count">
                          <TextFieldsIcon sx={{ fontSize: 15 }} />
                          <span className="sd-card-stat-value">{totalWords}</span>
                        </span>
                      </>
                    )}
                    {(() => {
                      const ts = script?.updated_at || script?.created_at;
                      if (!ts) return null;
                      const d = new Date(ts);
                      const formatted = d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
                      return (
                        <>
                          <span className="sd-card-stat sd-card-stat--desktop">
                            <ScheduleIcon sx={{ fontSize: 14, mr: 0.3, verticalAlign: "text-top" }} /> {formatted}
                          </span>
                          <span className="sd-card-stat sd-card-stat--mobile" title="Last updated">
                            <ScheduleIcon sx={{ fontSize: 15 }} />
                            <span className="sd-card-stat-value">{formatted}</span>
                          </span>
                        </>
                      );
                    })()}
                  </div>
                  <div className="sd-card-action-group">
                    <Tooltip title="Manual edit">
                      <button
                        type="button"
                        className="sd-card-action-btn"
                        aria-label="Manual edit"
                        onClick={() => setManualEditOpen(true)}
                      >
                        <span className="sd-card-action--desktop">
                          <EditOutlinedIcon sx={{ fontSize: 14, mr: 0.4, verticalAlign: "text-top" }} /> Manual Edit
                        </span>
                        <span className="sd-card-action--mobile">
                          <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </span>
                      </button>
                    </Tooltip>
                    <Tooltip title={copied ? "Copied" : "Copy script"}>
                      <button
                        type="button"
                        className="sd-card-action-btn"
                        aria-label={copied ? "Copied" : "Copy script"}
                        onClick={handleCopy}
                      >
                        <span className="sd-card-action--desktop">
                          {copied ? (
                            <>
                              <ContentCopyIcon sx={{ fontSize: 14, mr: 0.4, verticalAlign: "text-top" }} /> Copied
                            </>
                          ) : (
                            <>
                              <ContentCopyIcon sx={{ fontSize: 14, mr: 0.4, verticalAlign: "text-top" }} /> Copy
                            </>
                          )}
                        </span>
                        <span className="sd-card-action--mobile">
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </span>
                      </button>
                    </Tooltip>
                  </div>
                </div>
                <hr className="sd-divider" />
                <div className="sd-blocks-wrap">
                {blocks.map((block: any, bi: number) => {
                  const blockType = block.block_type;
                  const blockKey = block.id || `block-${bi}`;
                  const displayName = BLOCK_LABELS[blockType] || blockType.replace(/_/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
                  const chips = refineSuggestions[displayName] || [];
                  const isExpanded = expandedRefineBlocks.has(blockKey);
                  const blockText = blockInstructions[blockKey] || "";
                  return (
                  <div key={blockKey} className={`sd-section${isExpanded ? " sd-section-expanded" : ""}`}>
                    <div className="sd-section-header">
                      <div className="sd-section-label">
                        {displayName}
                      </div>
                      {chips.length > 0 && (
                        <button className={`sd-block-refine-btn${isExpanded ? " expanded" : ""}`} onClick={() => setExpandedRefineBlocks((prev) => { const next = new Set(prev); if (next.has(blockKey)) { next.delete(blockKey); setBlockInstructions((b) => { const copy = { ...b }; delete copy[blockKey]; return copy; }); } else { next.add(blockKey); } return next; })}>
                          {isExpanded ? "✕ Close" : <><AutoAwesomeIcon sx={{ fontSize: 12, mr: 0.3, verticalAlign: "text-top" }} /> Refine</>}
                        </button>
                      )}
                    </div>
                    <div className="sd-block-fields">
                      {block.title && (
                        <div className="sd-block-field">
                          <span className="sd-field-badge sd-field-badge-t">T</span>
                          <span className="sd-block-field-title">{block.title}</span>
                        </div>
                      )}
                      {block.narration && (
                        <div className="sd-block-field">
                          <span className="sd-field-badge sd-field-badge-n">N</span>
                          <span className="sd-block-field-text">{block.narration}</span>
                        </div>
                      )}
                      {block.caption && (
                        <div className="sd-block-field">
                          <span className="sd-field-badge sd-field-badge-c">C</span>
                          <span className="sd-block-field-caption">{block.caption}</span>
                        </div>
                      )}
                    </div>
                    {isExpanded && (
                      <div className="sd-block-refine-panel">
                        <textarea
                          className="sd-block-refine-textarea"
                          placeholder="Describe how you'd like to improve this block..."
                          rows={3}
                          maxLength={500}
                          value={blockText}
                          onChange={(e) => setBlockInstructions((prev) => ({ ...prev, [blockKey]: e.target.value }))}
                        />
                        {chips.length > 0 && (
                        <div className="sd-block-refine-row">
                          <div className="sd-refine-group-chips">
                            {chips.map((chip: any) => (
                              <span
                                key={chip.label}
                                className={`sd-refine-chip${blockText.includes(chip.instruction) ? " selected" : ""}`}
                                onClick={() => toggleChip(blockKey, chip.instruction)}
                              >
                                {(() => {
                                  const Icon = getRefinementIcon(chip.icon);
                                  return Icon ? React.createElement(Icon, { sx: { fontSize: 14, mr: 0.4, verticalAlign: "text-top" } }) : null;
                                })()}
                                {chip.label}
                              </span>
                            ))}
                          </div>
                          <span className="sd-block-refine-note">Only this block will be regenerated. The remaining blocks will stay exactly the same.</span>
                        </div>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })}
                </div>
              </div>

              <div className="sd-footer-actions">
                {blockInstructionList.length > 0 && (
                  <button
                    className="cv-btn cv-btn-primary sd-refine-submit"
                    onClick={handleBlockRefine}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "text-top" }} /> Refine Selected Blocks
                  </button>
                )}
                <button
                  className="sd-btn-link"
                  onClick={handleTurnToVideo}
                  disabled={turningToVideo}
                  style={turningToVideo ? { opacity: 0.6, cursor: "wait" } : undefined}
                >
                  <SmartDisplayOutlinedIcon sx={{ fontSize: 16, mr: 0.4, verticalAlign: "text-top" }} />
                  {turningToVideo ? "Creating project..." : "Turn this script into a video →"}
                </button>
              </div>
          </div>
        </section>
      </div>
      </div>
    </>
  );
};

export default ScriptDetail;
